import { Box, MenuItem, TextField } from "@mui/material"

import { makeStyles } from "makeStyles"

import LockingMechanismConfiguration from "./subComponents/LockingMechanismConfiguration"

import useSystemsConfiguration from "@secureo/common/hooks/cylinderConfigurations/useSystemsConfiguration"
import useOuterLengthsBySystemConfiguration from "@secureo/common/hooks/cylinderConfigurations/useOuterLengthsBySystemConfiguration"
import useLengthCombinationsBySystemConfiguration from "@secureo/common/hooks/cylinderConfigurations/useLengthCombinationsBySystemConfiguration"

import {
	DispatchProductDetailPageReducer,
	ProductDetailPageReducerState,
	setSelectedSystem,
	setSelectedOuterLength,
	setSelectedInnerLength,
} from "@secureo/common/reducers/productDetailPageReducer"
import {
	OuterLengthsBySystem,
	SystemCylinderConfiguration,
	LengthCombinationsBySystem,
} from "@secureo/common/typings/Product"

const useStyles = makeStyles()(() => ({
	formControl: {
		minWidth: 230,
	},
}))

interface SystemsAndOuterAndInnerLengthConfigurationProps {
	lengthCombinationsBySystem: LengthCombinationsBySystem
	selectedSystem: string
	selectedOuterLength: number
	selectedInnerLength: number
	onSystemChange: (selectedSystem: string) => void
	onOuterLengthChange: (selectedOuterLength: number) => void
	onInnerLengthChange: (selectedInnerLength: number) => void
	systemConfigurationToolTip?: string
	disabled?: boolean
}

const SystemsAndOuterAndInnerLengthConfiguration = ({
	lengthCombinationsBySystem,
	selectedSystem,
	selectedOuterLength,
	selectedInnerLength,
	onSystemChange,
	onOuterLengthChange,
	onInnerLengthChange,
	disabled,
}: // systemConfigurationToolTip,
SystemsAndOuterAndInnerLengthConfigurationProps) => {
	const { classes } = useStyles()
	const {
		systemInputProps,
		outerLengthInputProps,
		innerLengthInputProps,
		availableOuterLengths,
		availableInnerLengths,
		availableSystems,
	} = useLengthCombinationsBySystemConfiguration(
		lengthCombinationsBySystem,
		selectedSystem,
		selectedOuterLength,
		selectedInnerLength,
		onSystemChange,
		onOuterLengthChange,
		onInnerLengthChange,
	)

	return (
		<>
			<TextField
				id="system"
				label="Ausführung"
				className={classes.formControl}
				value={systemInputProps.value}
				onChange={(event) => systemInputProps.onChange(null, event.target.value)}
				margin="normal"
				variant="outlined"
				select
				disabled={disabled}
			>
				{availableSystems.map((system) => (
					<MenuItem key={system} value={system}>
						{system}
					</MenuItem>
				))}
			</TextField>
			<div style={{ display: "flex", alignItems: "center" }}>
				<div style={{ width: "50%" }}>
					<TextField
						id="outerLength"
						className={classes.formControl}
						label="Außenlänge"
						{...outerLengthInputProps}
						margin="normal"
						variant="outlined"
						select
						disabled={disabled}
					>
						{availableOuterLengths.map((outerLength) => (
							<MenuItem key={outerLength} value={outerLength}>
								{outerLength} mm
							</MenuItem>
						))}
					</TextField>
				</div>
				<Box ml={1} style={{ width: "50%" }}>
					<TextField
						id="innerLength"
						className={classes.formControl}
						label="Innenlänge"
						{...innerLengthInputProps}
						margin="normal"
						variant="outlined"
						select
						disabled={disabled}
					>
						{availableInnerLengths.map((innerLength) => (
							<MenuItem key={innerLength} value={innerLength}>
								{innerLength} mm
							</MenuItem>
						))}
					</TextField>
				</Box>
			</div>
		</>
	)
}

interface SystemsAndOuterLengthConfigurationProps {
	outerLengthsBySystem: OuterLengthsBySystem
	selectedSystem: string
	selectedOuterLength: number
	onSystemChange: (selectedSystem: string) => void
	onOuterLengthChange: (selectedOuterLength: number) => void
	systemConfigurationToolTip?: string
	disabled?: boolean
}

const SystemsAndOuterLengthConfiguration = ({
	outerLengthsBySystem,
	selectedSystem,
	selectedOuterLength,
	onSystemChange,
	onOuterLengthChange,
	// systemConfigurationToolTip,
	disabled,
}: SystemsAndOuterLengthConfigurationProps) => {
	const { classes } = useStyles()
	const { systemInputProps, outerLengthInputProps, availableSystems, availableOuterLengths } =
		useOuterLengthsBySystemConfiguration(
			outerLengthsBySystem,
			selectedSystem,
			selectedOuterLength,
			onSystemChange,
			onOuterLengthChange,
		)

	return (
		<>
			<TextField
				id="system"
				label="Ausführung"
				className={classes.formControl}
				select
				value={systemInputProps.value}
				onChange={(event) => systemInputProps.onChange(null, event.target.value)}
				margin="normal"
				variant="outlined"
				disabled={disabled}
			>
				{availableSystems.map((system) => (
					<MenuItem key={system} value={system}>
						{system}
					</MenuItem>
				))}
			</TextField>
			<TextField
				id="outerLength"
				className={classes.formControl}
				label="Außenlänge"
				select
				{...outerLengthInputProps}
				margin="normal"
				variant="outlined"
				disabled={disabled}
			>
				{availableOuterLengths.map((outerLength) => (
					<MenuItem key={outerLength} value={outerLength}>
						{outerLength} mm
					</MenuItem>
				))}
			</TextField>
		</>
	)
}

interface SystemsConfigurationProps {
	systems: SystemCylinderConfiguration[]
	selectedSystem: string
	onSystemChange: (selectedSystem: string) => void
	disabled?: boolean
	systemConfigurationToolTip?: string
}

const SystemsConfiguration = ({
	systems,
	selectedSystem,
	onSystemChange,
	// systemConfigurationToolTip,
	disabled = false,
}: SystemsConfigurationProps) => {
	const { classes } = useStyles()
	const { systemInputProps, availableSystem } = useSystemsConfiguration(
		systems,
		selectedSystem,
		onSystemChange,
	)

	return (
		<TextField
			id="system"
			label="Ausführung"
			className={classes.formControl}
			select
			value={systemInputProps.value}
			onChange={(event) => systemInputProps.onChange(event.target.value)}
			margin="normal"
			variant="outlined"
			disabled={disabled}
		>
			{availableSystem.map(({ system }) => (
				<MenuItem key={system} value={system}>
					{system}
				</MenuItem>
			))}
		</TextField>
	)
}

const SystemAndLengthSelection = ({ accessToken, reducerState, dispatch }: Props) => {
	const {
		isLoading,
		selectedSystem,
		selectedOuterLength,
		selectedInnerLength,
		cylinderLockingMechanismProducts,
		cylinderConfigurationType,
		attributes: {
			systems,
			outerLengthsBySystem,
			lengthCombinationsBySystem,
			systemConfigurationToolTip,
		},
	} = reducerState

	const onSystemChange = (selectedSystem: string) => {
		setSelectedSystem(accessToken, dispatch, reducerState, selectedSystem)
	}

	const onOuterLengthChange = (selectedOuterLength: number) => {
		setSelectedOuterLength(accessToken, dispatch, reducerState, selectedOuterLength)
	}

	const onInnerLengthChange = (selectedInnerLength: number) => {
		setSelectedInnerLength(accessToken, dispatch, reducerState, selectedInnerLength)
	}

	return (
		<div>
			<div style={{ display: "flex", alignItems: "center", flexFlow: "wrap" }}>
				{cylinderConfigurationType === "systems" && (
					<SystemsConfiguration
						systems={systems}
						selectedSystem={selectedSystem}
						onSystemChange={onSystemChange}
						systemConfigurationToolTip={systemConfigurationToolTip}
						disabled={isLoading || !accessToken}
					/>
				)}
				{cylinderConfigurationType === "outerLengthsBySystem" && (
					<SystemsAndOuterLengthConfiguration
						outerLengthsBySystem={outerLengthsBySystem}
						selectedSystem={selectedSystem}
						selectedOuterLength={selectedOuterLength}
						onSystemChange={onSystemChange}
						onOuterLengthChange={onOuterLengthChange}
						systemConfigurationToolTip={systemConfigurationToolTip}
						disabled={isLoading || !accessToken}
					/>
				)}
				{cylinderConfigurationType === "lengthCombinationsBySystem" && (
					<SystemsAndOuterAndInnerLengthConfiguration
						lengthCombinationsBySystem={lengthCombinationsBySystem}
						selectedSystem={selectedSystem}
						selectedOuterLength={selectedOuterLength}
						selectedInnerLength={selectedInnerLength}
						onSystemChange={onSystemChange}
						onOuterLengthChange={onOuterLengthChange}
						onInnerLengthChange={onInnerLengthChange}
						systemConfigurationToolTip={systemConfigurationToolTip}
						disabled={isLoading || !accessToken}
					/>
				)}
			</div>

			{cylinderLockingMechanismProducts && (
				<LockingMechanismConfiguration dispatch={dispatch} reducerState={reducerState} />
			)}
		</div>
	)
}

interface Props {
	accessToken: string
	dispatch: DispatchProductDetailPageReducer
	reducerState: ProductDetailPageReducerState
}

export default SystemAndLengthSelection
